import { useCallback } from 'react'

import { HrefLink, Notification, NotificationWithTextbox } from '@smartbear/swagger-portal-component-library'
import { clsx } from 'clsx'
import { Toast, toast, useToaster } from 'react-hot-toast/headless'
import { Trans, TransProps, useTranslation } from 'react-i18next'

import { DetailedErrorMessage } from '~/shared/components/Notifications/DetailedErrorMessage'
import { useNotificationObserver } from '~/shared/components/Notifications/useNotificationObserver.ts'
import { NotificationConfig } from '~/shared/types/notifications.ts'

const isTransObject = (obj: any): obj is TransProps<string> => typeof obj === 'object' && obj?.i18nKey

const NotificationContent = ({ message }: { message: NotificationConfig['message'] }) => {

  if (isTransObject(message)) {
    return <Trans 
      components={{
        strong: <span className="font-semibold" />,
        HrefLink: <HrefLink isExternal={true} size="small" target="_blank" />,
      }}
      {...message}
    />
  }
  return message
}

export const Notifications = () => {
  const { t } = useTranslation()

  const { handlers, toasts } = useToaster()

  const { calculateOffset, endPause, startPause, updateHeight } = handlers

  const { clearRefOnDismissNotification } = useNotificationObserver()

  const renderToast = useCallback((toastSlice: Toast) => {
    const {
      dismiss,
      message,
      style,
      type,
      detailedMessage,
    } = (typeof toastSlice.message === 'string' ? JSON.parse(toastSlice.message as string) : toastSlice.message) as NotificationConfig

    const dismissId = dismiss ? toastSlice.id : null

    let dismissHandler: (() => void) | undefined

    if (dismissId) {
      dismissHandler = () => {
        clearRefOnDismissNotification?.()
        toast.dismiss(dismissId)
      }
    }

    if (detailedMessage) {
      return <NotificationWithTextbox
        type={type}
        className={clsx(
          'w-[calc(100vw-48px)] max-w-[400px] md:min-w-[350px] 2xl:max-w-[600px] md:w-max items-start',
          style
        )}
        textboxMessage={<DetailedErrorMessage message={detailedMessage} />}
        message={message}
        dismissHandler={dismissHandler}
        translations={{ textboxLabel: t('components.ui.notifications.textboxLabels.errorMessage') }}
      />
    }
    return <Notification
      type={type}
      className={clsx(
        'w-[calc(100vw-48px)] max-w-[400px] md:min-w-[350px] 2xl:max-w-[600px] md:w-max items-start',
        style
      )}
      dismissHandler={dismissHandler}
    >
      <NotificationContent message={message} />
    </Notification>

  }, [toasts])

  return (
    <div onMouseEnter={startPause} onMouseLeave={endPause}
      className="fixed flex w-screen justify-center top-[36px] md:top-[26px] z-[51]"
    >
      {toasts
        .filter(toast => toast.visible)
        .map((toast) => {
          const offset = calculateOffset(toast, {
            reverseOrder: false,
            gutter: 8,
          })
          const ref = (el) => {
            if (el && typeof toast.height !== 'number') {
              const height = el.getBoundingClientRect().height
              updateHeight(toast.id, height)
            }
          }
          return (
            <div
              key={toast.id}
              ref={ref}
              className="absolute"
              style={{
                transition: 'all 0.5s ease-out',
                opacity: toast.visible ? 1 : 0,
                transform: `translateY(${offset}px)`,
              }}
              {...toast.ariaProps}
            >
              {renderToast(toast)}
            </div>
          )
        })}
    </div>
  )
}